/**
 * Главная страница репо: top-bar + side-panel + лента блоков.
 *
 * Reading-flow страницы (по логике стратегического документа, не по
 * type'ам сущностей) — отвечает на «что мы решили и как к этому пришли»:
 *
 *   1. Шапка Юнита (метрики + имя)
 *   2. **О ПРОЕКТЕ (frame)** — след решения целиком:
 *        intro (что за проект) / market (что делает рынок) /
 *        frames (что решили до рынка) / decisions (что решили и почему) /
 *        open-questions (что ещё не решили — пара к предыдущему)
 *   3. **КАНВАСЫ** — во что эти решения превратились в продукте,
 *      по 9 блоков на каждый Канвас; на сущностях — значки-обоснования
 *      (🔀 решение / ⚖️ рамка / 📡 сигнал)
 *   4. **ПОДДЕРЖКА (backstage)** — на чём решения держатся:
 *      evidence / validation. Справочный материал, в который заходят
 *      по значку с конкретного узла, а не читают линейно.
 *
 * Раньше все 10 project-нарративов шли подряд ДО Канвасов: читатель
 * получал «список Ресурсов и Партнёров» прежде, чем понимал «что
 * продаём». Новый порядок ставит акт-связку перед канвасами, а
 * «бэкстейдж» — после.
 *
 * Sidebar: desktop — sticky aside 280px; mobile — Mantine `<Drawer>`,
 * открывается из hamburger'а в TopBar.
 * EntityCard drawer и flash-эффект — на уровне страницы, один раз.
 */

import { Box, Divider, Drawer, Group, Stack, Text, Title } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { FRAME_IDS } from '@pflow/shared/v2';
import type { CanvasView, NarrativeAspect, ProjectNarrativeAspectId, Snapshot } from '@pflow/shared/v2';
import { TopBar } from './TopBar';
import { GlossarySidebar } from './GlossarySidebar';
import { NarrativeAspectView } from './NarrativeAspectView';
import { EntityCard } from './EntityCard';
import { SelectionActions } from './SelectionActions';
import { useUiStore } from '../stores/ui-store';
import { useFlashEffect } from '../hooks/use-flash-effect';
import { DESKTOP_QUERY } from '../lib/breakpoints';


interface Props {
    snapshot:    Snapshot;
    currentRef:  string | undefined;
    onRefChange: (ref: string | undefined) => void;
    currentPath: string | undefined;
    onSync?:     () => void;
    syncStatus?: 'idle' | 'syncing';
    syncError?:  string;
    onPrint?:    () => void;
}

const SIDEBAR_WIDTH = 280;
const HEADER_HEIGHT = 64;

export function CanvasViewPage({ snapshot, currentRef, onRefChange, currentPath, onSync, syncStatus, syncError, onPrint }: Props): JSX.Element {
    const isDesktop   = useMediaQuery(DESKTOP_QUERY, true);
    const sidebarOpen = useUiStore(s => s.sidebarOpen);
    const closeSidebar = useUiStore(s => s.closeSidebar);
    useFlashEffect();

    const sidebarContent = (
        <GlossarySidebar
            projectAspects={snapshot.projectAspects}
            canvases={snapshot.canvases}
            glossary={snapshot.glossary}
            signals={snapshot.signals}
            owner={snapshot.meta.owner}
            repo={snapshot.meta.repo}
            path={snapshot.meta.subpath || undefined}
            onNavigate={isDesktop ? undefined : closeSidebar}
            scrollHeight={isDesktop ? `calc(100vh - ${HEADER_HEIGHT + 46}px)` : 'calc(100vh - 100px)'}
        />
    );

    return (
        <Box>
            <TopBar
                snapshot={snapshot}
                currentRef={currentRef}
                onRefChange={onRefChange}
                onSync={onSync}
                syncStatus={syncStatus}
                syncError={syncError}
                onPrint={onPrint}
            />
            <Group align="flex-start" gap={0} style={{ alignItems: 'stretch' }}>
                {isDesktop && (
                    <Box
                        component="aside"
                        w={SIDEBAR_WIDTH}
                        style={{
                            position: 'sticky',
                            top: HEADER_HEIGHT,
                            height: `calc(100vh - ${HEADER_HEIGHT}px)`,
                            borderRight: '1px solid var(--mantine-color-default-border)',
                            background: 'var(--mantine-color-body)',
                            flexShrink: 0,
                        }}
                    >
                        {sidebarContent}
                    </Box>
                )}
                <Box data-quote-scope style={{
                    flex: 1,
                    minWidth: 0,
                    maxWidth: 920,
                    margin: '0 auto',
                    padding: isDesktop ? '24px 16px' : '16px 12px',
                }}>
                    <UnitHeader snapshot={snapshot} />
                    <FrameSection aspects={frameAspects(snapshot.projectAspects)} />
                    {snapshot.canvases.map(c => (
                        <CanvasSection key={c.name} canvas={c} />
                    ))}
                    <BackstageSection aspects={backstageAspects(snapshot.projectAspects)} />
                </Box>
            </Group>

            {!isDesktop && (
                <Drawer
                    opened={sidebarOpen}
                    onClose={closeSidebar}
                    position="left"
                    // На phone (≤480px) drawer 85% ширины — оставляет полоску
                    // основного контента справа, чтобы понятно было что
                    // тапнуть для закрытия. На tablet (481-1023px) — 320px.
                    size="85%"
                    withCloseButton
                    title="Навигация"
                    styles={{ body: { padding: 0 } }}
                >
                    {sidebarContent}
                </Drawer>
            )}

            <EntityCard
                owner={snapshot.meta.owner}
                repo={snapshot.meta.repo}
                gitRef={currentRef}
                sha={snapshot.meta.sha}
                path={currentPath}
            />
            <SelectionActions />
        </Box>
    );
}

function UnitHeader({ snapshot }: { snapshot: Snapshot }): JSX.Element {
    const isDesktop = useMediaQuery(DESKTOP_QUERY, true);
    if (!snapshot.unit) {
        return (
            <Box mb={isDesktop ? 'xl' : 'lg'}>
                <Title order={isDesktop ? 2 : 3} style={{ wordBreak: 'break-word' }}>
                    {snapshot.meta.owner}/{snapshot.meta.repo}
                </Title>
                <Text c="dimmed">Юнит не объявлен.</Text>
            </Box>
        );
    }
    return (
        <Box mb={isDesktop ? 'xl' : 'lg'}>
            <Title order={isDesktop ? 2 : 3} style={{ wordBreak: 'break-word' }}>
                Юнит «{snapshot.unit.name}»
            </Title>
            {snapshot.unit.metrics.length > 0 && (
                <Group gap={isDesktop ? 'md' : 'sm'} mt={6} wrap="wrap">
                    {snapshot.unit.metrics.map((m, i) => (
                        <Box key={i}>
                            <Text fz="xs" c="dimmed" tt="uppercase">{m.label}</Text>
                            <Text fw={600}>{m.value}</Text>
                        </Box>
                    ))}
                </Group>
            )}
        </Box>
    );
}

function frameAspects(all: NarrativeAspect[]): NarrativeAspect[] {
    return all.filter(a => FRAME_IDS.has(a.id as ProjectNarrativeAspectId));
}

function backstageAspects(all: NarrativeAspect[]): NarrativeAspect[] {
    return all.filter(a => !FRAME_IDS.has(a.id as ProjectNarrativeAspectId));
}

function FrameSection({ aspects }: { aspects: NarrativeAspect[] }): JSX.Element | null {
    const isDesktop = useMediaQuery(DESKTOP_QUERY, true);
    if (aspects.length === 0) return null;
    return (
        <Box mb={isDesktop ? 'xl' : 'lg'}>
            <Title order={isDesktop ? 3 : 4} mb={isDesktop ? 'md' : 'sm'} id="section-project">
                О проекте
            </Title>
            <Stack gap="xs">
                {aspects.map(a => <NarrativeAspectView key={a.id} aspect={a} />)}
            </Stack>
        </Box>
    );
}

function BackstageSection({ aspects }: { aspects: NarrativeAspect[] }): JSX.Element | null {
    const isDesktop = useMediaQuery(DESKTOP_QUERY, true);
    if (aspects.length === 0) return null;
    // Все блоки с пустыми blocks и без summary — скрываем целиком, чтобы
    // не превращать backstage в кладбище плейсхолдеров.
    const visible = aspects.filter(a => a.blocks.length > 0 || a.summary !== null);
    if (visible.length === 0) return null;
    return (
        <Box mt={isDesktop ? 'xl' : 'lg'}>
            <Divider my={isDesktop ? 'lg' : 'md'} />
            <Title order={isDesktop ? 3 : 4} mb={isDesktop ? 'md' : 'sm'} id="section-backstage">
                Поддержка
            </Title>
            <Text fz="sm" c="dimmed" mb="sm">
                Доказательная база, ресурсы, портфельные связи — материал,
                который обосновывает страницу выше.
            </Text>
            <Stack gap="xs">
                {visible.map(a => <NarrativeAspectView key={a.id} aspect={a} />)}
            </Stack>
        </Box>
    );
}

function CanvasSection({ canvas }: { canvas: CanvasView }): JSX.Element {
    const isDesktop = useMediaQuery(DESKTOP_QUERY, true);
    const id = `section-canvas-${canvas.name.toLowerCase().replace(/[^a-zа-я0-9]+/gi, '-')}`;
    return (
        <Box mb={isDesktop ? 'xl' : 'lg'}>
            <Divider my={isDesktop ? 'lg' : 'md'} />
            <Box id={id} mb={isDesktop ? 'md' : 'sm'} style={{ scrollMarginTop: 80 }}>
                <Title order={isDesktop ? 3 : 4} style={{ wordBreak: 'break-word' }}>
                    🎨 Канвас «{canvas.name}»
                </Title>
                {canvas.metrics.length > 0 && (
                    <Group gap={isDesktop ? 'md' : 'sm'} mt={6} wrap="wrap">
                        {canvas.metrics.map((m, i) => (
                            <Text key={i} fz="sm" c="dimmed">
                                <strong>{m.label}:</strong> {m.value}
                            </Text>
                        ))}
                    </Group>
                )}
            </Box>
            <Stack gap="xs">
                {canvas.aspects.map(a => (
                    <NarrativeAspectView key={a.id} aspect={a} />
                ))}
            </Stack>
        </Box>
    );
}
